import { forOwn, mapValues } from 'lodash'

import { Ad, IAd } from '@/common/models/data/dashboard/ads'
import { Category, ICategory } from '@/common/models/data/dashboard/posts/category'
import { CollaborationEntry, ICollaborationEntry } from '@/common/models/data/dashboard/collaboration/collaboration-entry'
import { ContactEntry, IContactEntry } from '@/common/models/data/dashboard/contact'
import { Download, IDownload } from '@/common/models/data/dashboard/downloads/download'
import { IPage, Page } from '@/common/models/data/dashboard/page'
import { IPaginatedResponse } from '@/common/models/data/common/pagination/paginated-response'
import { IPost, Post } from '@/common/models/data/dashboard/posts/post'
import { IPriceListEntry, PriceListEntry } from '@/common/models/data/dashboard/price-list'
import { ISchedule, Schedule } from '@/common/models/data/dashboard/schedule/schedule'
import { ISlide, Slide } from '@/common/models/data/dashboard/slides'
import { IStaff, Staff } from '@/common/models/data/dashboard/staff/staff'
import { ITag, Tag } from '@/common/models/data/dashboard/posts/tag'
import { IVoiceBankEntry, VoiceBankEntry } from '@/common/models/data/dashboard/voice-bank/voice-bank-entry'
import { IVoiceBankLanguage, VoiceBankLanguage } from '@/common/models/data/dashboard/voice-bank/voice-bank-language'
import { IVoiceBankType, VoiceBankType } from '@/common/models/data/dashboard/voice-bank/voice-bank-type'

import { PaginatedResponse } from '@/common/models/data/common/pagination/paginated-response'
import { IHot17IndexResponse, Hot17IndexResponse } from '@/common/models/responses/hot17-index-response'
import { IPostShowResponse, PostShowResponse } from '@/common/models/responses/post-show-response'

import { useAdsStore } from '@/app/stores/ads'
import { useBlogStore } from '@/app/stores/blog'
import { useCategoriesStore } from '@/app/stores/categories'
import { useCollaborationStore } from '@/app/stores/collaboration'
import { useContactStore } from '@/app/stores/contact'
import { useHot17Store } from '@/app/stores/hot-17'
import { useDownloadsStore } from '@/app/stores/downloads'
import { useNewsStore } from '@/app/stores/news'
import { usePagesStore } from '@/app/stores/pages'
import { usePostsStore } from '@/app/stores/posts'
import { usePriceListStore }  from '@/app/stores/price-list'
import { useScheduleStore } from '@/app/stores/schedule'
import { useSearchStore } from '@/app/stores/search'
import { useSharedSettingsStore } from '@/common/stores/shared-settings'
import { useSlidesStore } from '@/app/stores/slides'
import { useStaffStore } from '@/app/stores/staff'
import { useTagsStore } from '@/app/stores/tags'
import { useVoiceBankStore } from '@/app/stores/voice-bank'

type Context = {
    blog?: {
        categorized?: {
            categories: Array<ICategory>
            entries: IPaginatedResponse<IPost>
            slug: string
        }
        entries?: IPaginatedResponse<IPost>
        show?: IPostShowResponse
    }
    categories?: Array<ICategory>
    collaboration?: Array<ICollaborationEntry>
    contact?: Array<IContactEntry>
    dates?: Array<string>
    downloads?: Array<IDownload>
    editorial_college?: Array<IStaff>
    home?: {
        ads: Array<IAd>
        posts: IPaginatedResponse<IPost>
        slides: Array<ISlide>
    }
    hot17?: IHot17IndexResponse
    news?: {
        entries?: IPaginatedResponse<IPost>
        show?: IPostShowResponse
    }
    pages?: Record<string, IPage>
    posts?: {
        published_at?: {
            date: string
            entries: IPaginatedResponse<IPost>
        }
        published_by?: {
            author_id: number
            entries: IPaginatedResponse<IPost>
        }
        tagged?: {
            tag: ITag
            entries: IPaginatedResponse<IPost>
        }
    }
    price_list?: Array<IPriceListEntry>
    schedule?: Record<string, Array<ISchedule>>
    search?: {
        entries: Array<IPost>
        query: string
    }
    shared_settings?: Record<string, string>
    staff?: Array<IStaff>
    voice_bank?: {
        entries: Array<IVoiceBankEntry>
        languages: Array<IVoiceBankLanguage>
        types: Array<IVoiceBankType>
    }
}

const setState = (context: Context): void => {
    // Common
    if (context.shared_settings) {
        useSharedSettingsStore().settings = context.shared_settings
    }

    if (context.pages) {
        const pagesStore = usePagesStore()

        forOwn(context.pages, page => {
            pagesStore.setPage(new Page(page))
        })
    }

    // /
    if (context.home) {
        useAdsStore().ads = context.home.ads.map(ad => new Ad(ad))
        usePostsStore().updateDataFrom(PaginatedResponse.fromInterface(context.home.posts, dto => new Post(dto)))
        useSlidesStore().slides = context.home.slides.map(slide => new Slide(slide))
    }

    // /search/{query?}
    if (context.search) {
        const searchStore = useSearchStore()

        searchStore.query = context.search.query
        searchStore.results = context.search.entries.map(post => new Post(post))
    }

    /**
     * /posts/author/{id}/{page?}
     * /posts/date/{date}/{page?}
     * /posts/dates
     * /posts/tag/{slug}/{page?}
     */
    if (context.posts) {
        const postsStore = usePostsStore()

        if (context.posts.published_by) {
            postsStore.setByUser({
                userId: context.posts.published_by.author_id,
                data: PaginatedResponse.fromInterface(context.posts.published_by.entries, dto => new Post(dto))
            })
        }

        if (context.posts.published_at) {
            postsStore.setByDate({
                date: new Date(Date.parse(context.posts.published_at.date)),
                data: PaginatedResponse.fromInterface(context.posts.published_at.entries, dto => new Post(dto))
            })
        }

        if (context.posts.tagged) {
            useTagsStore().tags.push(new Tag(context.posts.tagged.tag))
            postsStore.setByTag({
                tag: context.posts.tagged.tag.slug,
                data: PaginatedResponse.fromInterface(context.posts.tagged.entries, dto => new Post(dto))
            })
        }
    }

    if (context.dates) {
        usePostsStore().dates = context.dates
    }

    /**
     * /news/{page?}
     * /news/{slug}
     */
    if (context.news) {
        const newsStore = useNewsStore()

        if (context.news.entries) {
            newsStore.entries = PaginatedResponse.fromInterface(context.news.entries, dto => new Post(dto))
        }

        if (context.news.show) {
            newsStore.setBySlug(new PostShowResponse(context.news.show))
        }
    }

    // /schedule
    if (context.schedule) {
        useScheduleStore().schedule = mapValues(context.schedule, arr => {
            return arr.map(entry => new Schedule(entry))
        })
    }

    // /hot-17
    if (context.hot17) {
        useHot17Store().data = new Hot17IndexResponse(context.hot17)
    }

    /**
     * /blog/categories
     * /blog/{page?}
     * /blog/{slug}
     * /blog/category{slug}/{page?}
     */
    if (context.categories) {
        useCategoriesStore().categories = context.categories.map(category => new Category(category))
    }

    if (context.blog) {
        const blogStore = useBlogStore()

        if (context.blog.entries) {
            blogStore.entries = PaginatedResponse.fromInterface(context.blog.entries, dto => new Post(dto))
        }

        if (context.blog.show) {
            blogStore.setBySlug(new PostShowResponse(context.blog.show))
        }

        if (context.blog.categorized) {
            useCategoriesStore().categories = context.blog.categorized.categories.map(category => new Category(category))
            blogStore.setByCategory({
                slug: context.blog.categorized.slug,
                data: PaginatedResponse.fromInterface(context.blog.categorized.entries, dto => new Post(dto))
            })
        }
    }

    /**
     * /about
     * /about/crew
     * /about/editorial-college
     * /about/history
     */
    if (context.staff) {
        useStaffStore().staff = context.staff.map(entry => new Staff(entry))
    }

    if (context.editorial_college) {
        useStaffStore().editorialCollege = context.editorial_college.map(entry => new Staff(entry))
    }

    /**
     * /contact
     * /contact/cooperation
     * /contact/recruitment
     */
    if (context.contact) {
        useContactStore().entries = context.contact.map(entry => new ContactEntry(entry))
    }

    if (context.collaboration) {
        useCollaborationStore().entries = context.collaboration.map(entry => new CollaborationEntry(entry))
    }

    // /price-list
    if (context.price_list) {
        usePriceListStore().data = context.price_list.map(entry => new PriceListEntry(entry))
    }

    // /voice-bank
    if (context.voice_bank) {
        const voiceBankStore = useVoiceBankStore()

        voiceBankStore.types = context.voice_bank.types.map(type => new VoiceBankType(type))
        voiceBankStore.languages = context.voice_bank.languages.map(lang => new VoiceBankLanguage(lang))
        voiceBankStore.entries = context.voice_bank.entries.map(entry => new VoiceBankEntry(entry))
    }

    // /downloads
    if (context.downloads) {
        useDownloadsStore().entries = context.downloads.map(download => new Download(download))
    }
}

export { setState }
